/**
 * graphService.js — Microsoft Graph API client
 *
 * Makes authenticated requests to the Microsoft Graph REST API.
 * Used to read calendar events and write blocker events.
 *
 * All requests require an access token obtained from authService.
 * The token is passed in the Authorization header as a Bearer token.
 *
 * Graph API base URL: https://graph.microsoft.com/v1.0
 */

const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

/**
 * Fetch the authenticated user's profile.
 * Used to display the user's name and email in the UI
 * after login, confirming which account is connected.
 *
 * @param {string} accessToken - Bearer token from MSAL
 * @returns {object} User profile { displayName, mail, userPrincipalName }
 */
export async function getUserProfile(accessToken) {
  const response = await fetch(`${GRAPH_BASE_URL}/me`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch calendar events within a date range.
 * This is the core read operation for the consolidated calendar view.
 *
 * Uses the calendarView endpoint which automatically expands
 * recurring events into individual instances within the range —
 * no need to manually handle recurrence patterns.
 *
 * @param {string} accessToken - Bearer token from MSAL
 * @param {string} startDateTime - ISO 8601 start (e.g., '2026-03-14T00:00:00')
 * @param {string} endDateTime - ISO 8601 end (e.g., '2026-03-28T00:00:00')
 * @returns {Array} List of calendar events
 */
export async function getCalendarEvents(accessToken, startDateTime, endDateTime) {
  // calendarView automatically expands recurring events into instances
  // within the specified window. This is preferred over /events which
  // returns the series master and requires client-side expansion.
  const url = `${GRAPH_BASE_URL}/me/calendarView`
    + `?startDateTime=${encodeURIComponent(startDateTime)}`
    + `&endDateTime=${encodeURIComponent(endDateTime)}`
    + `&$select=id,subject,start,end,showAs,isAllDay,isCancelled,body`
    + `&$top=250`
    + `&$orderby=start/dateTime`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      // Request times in UTC — we handle timezone conversion client-side
      // using Luxon with IANA timezone names for correctness.
      Prefer: 'outlook.timezone="UTC"',
    },
  });

  if (!response.ok) {
    throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data.value || [];
}
